import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import TreeMenu from "react-simple-tree-menu";
import Paper from "@mui/material/Paper";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { supersetReportOperations } from "./duck/index";
import LoadingSpinner from "../common/components/LoadingSpinner";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Typography } from "@mui/material";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import Button from "@mui/material/Button";
import DownloadIcon from "@mui/icons-material/Download";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { ENVIRONMENT } from "../common/constants";
var rison = require('rison');

const SupersetReport = ({
  report,
  token,
  fetchReportDetails,
  fetchSupersetGuestToken,
  downloadSupersetReport,
  match,
  filter
}) => {
  const studyId = match.params.id;
  const [dashboardId, setDashboardId] = useState();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const frameContainer = useRef(null);

  useEffect(() => {
    const initSuperset = async () => {
      await fetchReportDetails(studyId);
     setLoading(false);
    };

    initSuperset();

    return () => setLoading(false);
  }, []);

  useEffect(() => {
  if (dashboardId) {
    fetchSupersetGuestToken(dashboardId, studyId);
  }
}, [dashboardId, studyId, fetchSupersetGuestToken]);

  useEffect(() => {
    if (!report) return;
    const hasPanel = !!(
      report.rows?.length &&
      report.rows[0].panels?.length &&
      report.rows[0].panels[0]
    );
    if (hasPanel) {
      // here 
      setDashboardId(report.rows[0].panels[0].url);
    }
  }, [report]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDownloadReport = async () => {
    if (!dashboardId) {
      setSnackbarMessage("No dashboard is available for download.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (downloading) return;

    try {
      setDownloading(true);
      await downloadSupersetReport(dashboardId, studyId);
    } catch (err) {
      setSnackbarMessage(
        err?.message || "Error downloading report. Please try again later."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDownloading(false);
    }
  };

  const setRef = (node) => {
    frameContainer.current = node;

    if (node) {
      const nativeFilters = {}
      if (filter.value) {
        const studyConfig = filter
        const values = [studyConfig.value]
        const filters = studyConfig.target ? [{ col: studyConfig.target, op: 'IN', val: values }] : []
        nativeFilters[studyConfig.id] = {
          id: studyConfig.id,
          extraFormData: { filters },
          filterState: { label: String(studyConfig.value), value: values, excludeFilterValues: true },
          ownState: {},
        }
      }

      embedDashboard({
        id: dashboardId,
        supersetDomain: ENVIRONMENT === "dev" ? "https://staging.superset.lumen-research.com" : "https://superset.lumen-research.com",
        mountPoint: node,
        fetchGuestToken: () => token,
        dashboardUiConfig: {
          hideTitle: true,
          hideChartControls: true,
          filters: {
            expanded: false,
            visible: true,
          },
          urlParams: {
            native_filters: rison.encode(nativeFilters),
          },
        },
      });
    }
  }

  if (report && !report.success)
    return (
      <Paper style={{ borderRadius: 16, height: "81vh" }}>
        <p
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "1.5rem",
            color: "#808080",
          }}
        >
          This {studyId ? "study" : "account"} has no reports
        </p>
      </Paper>
    );

  if (!report || !token || loading)
    return (
      <Paper style={{ borderRadius: 16, height: "81vh" }}>
        <LoadingSpinner style={{ padding: "32vh", margin: "auto" }} />
      </Paper>
    );

  const { rows } = report;

return (
  <div>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={20000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={handleSnackbarClose}
        severity={snackbarSeverity}
        variant="outlined"
        action={
          <Button color="inherit" size="small" onClick={handleSnackbarClose}>
            OK
          </Button>
        }
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "12px",
       marginRight: "68px",
      }}
    >
      <Button
        variant="contained"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadReport}
        disabled={!dashboardId || downloading}
        sx={{
          color: "#fff",
          "& .MuiButton-startIcon": {
            color: "#fff",
          },
        }}
      >
         {downloading ? "Downloading..." : "Download"}
      </Button>
    </div>

    <Paper
      style={{
        borderRadius: 16,
        height: "calc(100vh - 120px)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          height: "100%",
          padding: "16px",
          fontSize: "0.8rem",
          boxSizing: "border-box",
        }}
      >
        <div
          id="dashboard"
          style={{
            display: "flex",
            flex: "1 1 0%",
            flexGrow: "1",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "#d1d5db",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            id="superset-report-container"
            style={{ width: "100%", height: "100%" }}
            ref={setRef}
          ></div>
        </div>

        <div
          style={{
            overflowY: "scroll",
            flex: "0 1 auto",
            borderStyle: "solid",
            borderWidth: "1px",
            borderLeft: "none",
            borderColor: "#d1d5db",
          }}
        >
          <TreeMenu
            cacheSearch
            data={rows?.map((row) => {
              return {
                key: row?.name,
                label: row?.name,
                nodes: row?.panels?.map((panel) => {
                  return {
                    key: panel?.name,
                    label: panel?.name,
                    url: panel?.url,
                  };
                }),
              };
            })}
            initialFocusKey={`${rows?.[0]?.name}/${rows?.[0]?.panels?.[0]?.name}`}
            initialActiveKey={`${rows?.[0]?.name}/${rows?.[0]?.panels?.[0]?.name}`}
            initialOpenNodes={[rows?.[0]?.name]}
            debounceTime={125}
            disableKeyboard={false}
            hasSearch
            onClickItem={(item) => {
              if (item.url) {
                setDashboardId(item.url);
              }
            }}
            resetOpenNodesOnDataUpdate={false}
          />
        </div>
      </div>
    </Paper>
  </div>
);
};

const mapStateToProps = (state) => {
  return {
    report: state.supersetReportReducer.report,
    token: state.supersetReportReducer.token,
    filter: state.supersetReportReducer.filters
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchReportDetails: (studyId) =>
      dispatch(supersetReportOperations.fetchReportDetails(studyId)),
    fetchSupersetGuestToken: (panelUrl, studyId) =>
      dispatch(
        supersetReportOperations.fetchSupersetGuestToken(panelUrl, studyId),
      ),
    downloadSupersetReport: (dashboardId, studyId) =>
      dispatch(
        supersetReportOperations.downloadSupersetReport(dashboardId, studyId)
      ),
  };
};

SupersetReport.propTypes = {
  report: PropTypes.object,
  token: PropTypes.string,
  filter: PropTypes.object,
  fetchReportDetails: PropTypes.func,
  fetchSupersetGuestToken: PropTypes.func,
  downloadSupersetReport: PropTypes.func,
  match: PropTypes.object,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SupersetReport),
);
